import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronRight, Lock, ShieldCheck } from "lucide-react";

/**
 * DriverHOSAttentionWidget — custom renderer for widget_key = "driver_hos_attention".
 *
 * Aggregator payload:
 *   payload.data = { count, of, rows: [{driver_number, display_name,
 *                    current_duty_status, drive_available_minutes,
 *                    break_required_in_minutes, severity, last_updated_at}] }
 *
 * Data source: existing DemoMotiveProvider (read-only) exposed through the
 * /api/dashboard/summary aggregator. If the provider returns nothing usable,
 * the aggregator serves an `is_placeholder` payload — this widget honours
 * that with an honest empty state. No invented driver names, hours, or
 * violations.
 */

const formatWindow = (mins) => {
  if (mins === null || mins === undefined) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const Restricted = () => (
  <div
    data-testid="widget-driver_hos_attention"
    data-widget-tone="denied"
    className="bg-road-white/60 dark:bg-carbon/40 border border-dashed border-coach-silver dark:border-steel/40 rounded-md p-4 min-h-[172px] flex flex-col"
  >
    <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest text-steel">
      <Lock size={11} /> Driver HOS Attention
    </div>
    <div className="mt-2 text-[13px] font-medium text-steel dark:text-coach-silver">Restricted</div>
  </div>
);

const Placeholder = ({ reason }) => (
  <div
    data-testid="widget-driver_hos_attention"
    data-widget-tone="placeholder"
    className="bg-road-white/60 dark:bg-carbon/40 border border-dashed border-coach-silver dark:border-steel/40 rounded-md p-4 min-h-[172px] flex flex-col"
  >
    <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest text-steel">
      <AlertTriangle size={11} /> Driver HOS Attention
    </div>
    <div className="mt-2 text-[13px] font-medium text-steel dark:text-coach-silver"
         data-testid="widget-driver_hos_attention-placeholder">
      No HOS attention items currently available.
    </div>
    <div className="mt-1 text-[11px] text-steel">
      Telemetry source: Motive (read-only).
    </div>
  </div>
);

export default function DriverHOSAttentionWidget({ payload }) {
  if (payload?.unavailable_reason === "permission_denied") return <Restricted />;
  if (payload?.is_placeholder) return <Placeholder reason={payload?.unavailable_reason} />;

  const rows = payload?.data?.rows || [];
  const count = payload?.data?.count ?? rows.length;
  const of = payload?.data?.of;

  return (
    <div
      data-testid="widget-driver_hos_attention"
      className="bg-white dark:bg-graphite border border-coach-silver dark:border-graphite rounded-md p-4 min-h-[172px] flex flex-col"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest text-steel">
          <AlertTriangle size={11} className={count > 0 ? "text-critical-red" : "text-touring-lime"} />
          Driver HOS Attention
        </div>
        <div className="text-[10.5px] text-steel tabular-nums" data-testid="widget-driver_hos_attention-total">
          {count}{of !== undefined ? ` / ${of}` : ""} needing attention
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-1 text-[12px] text-steel"
             data-testid="widget-driver_hos_attention-empty">
          <ShieldCheck size={16} className="text-touring-lime" />
          All drivers within HOS limits.
        </div>
      ) : (
        <div className="mt-2 divide-y divide-chrome-mist dark:divide-graphite">
          {rows.map((r) => (
            <div key={r.motive_driver_id} className="py-2 flex items-center gap-2"
                 data-testid={`hos-row-${r.motive_driver_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] text-carbon dark:text-road-white truncate">
                  {r.display_name || r.driver_number || "—"}
                </div>
                <div className="text-[10.5px] text-steel truncate">
                  {r.driver_number}{r.current_duty_status ? ` · ${r.current_duty_status.replace(/_/g, " ")}` : ""}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[11px] text-critical-red tabular-nums" title="Drive time available">
                  {formatWindow(r.drive_available_minutes)} left
                </div>
                <div className="text-[10px] text-steel tabular-nums" title="Break required in">
                  break in {formatWindow(r.break_required_in_minutes)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Link
        to="/drivers"
        data-testid="widget-driver_hos_attention-view-all"
        className="mt-3 flex items-center justify-end gap-1 text-[11px] text-coach-blue hover:underline"
      >
        View Drivers <ChevronRight size={12} />
      </Link>
    </div>
  );
}
